import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * i18n primitives. UI copy must never be hardcoded; all strings are looked up
 * via `t()`. Missing keys fall back to English and then to the key itself.
 *
 * The full historical locale set (PRD FE-USER-SETTING-04) is enumerated in
 * `SUPPORTED_LOCALES`; English and Simplified Chinese ship with real catalogs,
 * the rest fall back to English until translated.
 */

export const SUPPORTED_LOCALES = [
  "ar", "bg", "ca", "cs", "da", "de", "el", "en", "en-GB", "es", "et", "fa", "fi",
  "fr", "gl", "hi", "hr", "hu", "id", "it", "ja", "ka-GE", "ko", "lt", "lv", "mr",
  "nb", "nl", "pl", "pt-BR", "pt-PT", "ro", "ru", "sk", "sl", "sr", "sv", "th",
  "tr", "uk", "vi", "zh-Hans", "zh-Hant",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const RTL_LOCALES: ReadonlySet<string> = new Set(["ar", "fa", "he", "ur"]);

export const LOCALE_LABELS: Record<Locale, string> = {
  ar: "العربية", bg: "Български", ca: "Català", cs: "Čeština", da: "Dansk",
  de: "Deutsch", el: "Ελληνικά", en: "English", "en-GB": "English (UK)",
  es: "Español", et: "Eesti", fa: "فارسی", fi: "Suomi", fr: "Français",
  gl: "Galego", hi: "हिन्दी", hr: "Hrvatski", hu: "Magyar", id: "Bahasa Indonesia",
  it: "Italiano", ja: "日本語", "ka-GE": "ქართული", ko: "한국어", lt: "Lietuvių",
  lv: "Latviešu", mr: "मराठी", nb: "Norsk bokmål", nl: "Nederlands", pl: "Polski",
  "pt-BR": "Português (BR)", "pt-PT": "Português (PT)", ro: "Română", ru: "Русский",
  sk: "Slovenčina", sl: "Slovenščina", sr: "Српски", sv: "Svenska", th: "ไทย",
  tr: "Türkçe", uk: "Українська", vi: "Tiếng Việt", "zh-Hans": "简体中文", "zh-Hant": "繁體中文",
};

type Catalog = Record<string, string>;

const catalogs: Partial<Record<Locale, Catalog>> = {
  en: {},
};

/** Register/merge a translation catalog (called by locale modules). */
export function registerCatalog(locale: Locale, catalog: Catalog): void {
  catalogs[locale] = { ...(catalogs[locale] ?? {}), ...catalog };
}

interface I18nState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useI18nStore = create<I18nState>()(
  persist(
    (set) => ({
      locale: "en",
      setLocale: (locale) => set({ locale }),
    }),
    { name: "memos-locale" },
  ),
);

/** Translate a key with optional {placeholder} interpolation. */
export function translate(locale: Locale, key: string, vars?: Record<string, string | number>): string {
  const table = catalogs[locale] ?? catalogs.en ?? {};
  let out = table[key] ?? catalogs.en?.[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      out = out.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return out;
}

/** Hook returning a bound translator + the active locale. */
export function useI18n() {
  const locale = useI18nStore((s) => s.locale);
  const setLocale = useI18nStore((s) => s.setLocale);
  return {
    locale,
    setLocale,
    t: (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars),
    isRtl: RTL_LOCALES.has(locale),
  };
}

/** Apply locale + text direction to <html>. */
export function applyLocaleToDocument(locale: Locale): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";
}
